// src/pages/Center Admin/LabPickupPage.tsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import LabPickUpActivePatientCard from "../../components/Center Admin/Reuse/LabPickUpActivePatientCard";
import CheckboxQueueSidebar, {
  type QueueItem,
} from "../../components/Center Admin/layout/CheckboxQueueSidebar";
import BnuSampleForm from "../../components/Center Admin/forms/sentToLab";
import "../../center admin css/labPickup.css";
import "../../center admin css/centerAdmin.css";

const LabPickupPage: React.FC = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [patientsData, setPatientsData] = useState<QueueItem[]>([]);
  const [activePatient, setActivePatient] = useState<QueueItem | null>(null);
  const [refreshQueueKey, setRefreshQueueKey] = useState(0);

  const handleSubmitSuccess = () => {
    setSelectedIds([]);
    setRefreshQueueKey((prev) => prev + 1);
  };

  /** ✅ Toggle checkbox selection */
  const toggle = (id: string) => {
    setSelectedIds((prev) => (prev[0] === id ? [] : [id]));
  };

  /** ✅ Handle queue data load safely */
  const handleItemsLoaded = useCallback((items: QueueItem[]) => {
    if (!items || !Array.isArray(items)) return;

    setPatientsData((prev) => {
      const sameLength = prev.length === items.length;
      const sameIds = sameLength && prev.every((p, i) => p.id === items[i].id);
      if (sameIds) return prev;
      return items;
    });
  }, []);

  /** ✅ Derive selected patients */
  const selectedPatients = useMemo(
    () => patientsData.filter((p) => selectedIds.includes(p.id)),
    [patientsData, selectedIds],
  );

  /** ✅ Global sync — listen for patient selection updates */
  useEffect(() => {
    const loadGlobalPatient = () => {
      const stored = sessionStorage.getItem("selectedPatient");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setActivePatient(parsed);
        } catch {
          setActivePatient(null);
        }
      } else {
        setActivePatient(null);
      }
    };

    loadGlobalPatient();

    const handlePatientUpdate = () => loadGlobalPatient();
    window.addEventListener("patientUpdated", handlePatientUpdate);
    window.addEventListener("storage", handlePatientUpdate);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdate);
      window.removeEventListener("storage", handlePatientUpdate);
    };
  }, []);

  return (
    <div className="lp-page">
      {/* ✅ Global Checkbox Queue Sidebar (replaces local queue) */}
      <CheckboxQueueSidebar
        selectedIds={selectedIds}
        onToggle={toggle}
        onItemsLoaded={handleItemsLoaded}
        refreshKey={refreshQueueKey}
      />

      <main className="lp-main">
        {/* ✅ Default empty state */}
        {selectedPatients.length === 0 && (
          <div className="lp-empty">
            Select one or more patients from the left.
          </div>
        )}

        {/* ✅ Single Patient Selected */}
        {selectedPatients.length === 1 && (
          <>
            <LabPickUpActivePatientCard
              key={selectedPatients[0].id}
              patient={{
                emrId: selectedPatients[0].emrId,
                name: selectedPatients[0].name,
                genderShort: selectedPatients[0].genderShort,
                ageYears: selectedPatients[0].ageYears,
                avatarUrl: selectedPatients[0].avatarUrl,
              }}
            />
            <div className="lp-under">
              <BnuSampleForm
                emrIds={
                  selectedPatients[0].emrId
                    ? [selectedPatients[0].emrId]
                    : [selectedPatients[0].id]
                }
                sessionId={selectedPatients[0].sessionId}
                onSuccess={handleSubmitSuccess}
              />
            </div>
          </>
        )}

        {/* ✅ Multiple Patients Selected */}
        {selectedPatients.length > 1 && (
          <div className="lp-two-col">
            <div className="lp-col-cards">
              {selectedPatients.map((p) => (
                <LabPickUpActivePatientCard
                  key={p.id}
                  patient={{
                    emrId: p.emrId,
                    name: p.name,
                    genderShort: p.genderShort,
                    ageYears: p.ageYears,
                    avatarUrl: p.avatarUrl,
                  }}
                />
              ))}
            </div>

            <div className="lp-col-question">
              <BnuSampleForm
                emrIds={selectedPatients.map((p) => p.emrId || p.id)}
                onSuccess={handleSubmitSuccess}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LabPickupPage;
